// /dashboards/:dashboardId — the wall surface.
//
// Shell from the v7 mockup:
//   1. DashboardSwitcher   carousel pinned to the top
//   2. v7-mode-bar         title + crumbs + View/Edit toggle
//   3. v7-wall (12×8)      grid of <CameraTile>s
//   4. right pane          "events" in view mode, "library" in edit mode
//
// View vs Edit is held in the URL query (`?mode=edit`) so a refresh
// preserves the operator's mode. Edit mode forks a working copy of
// the dashboard's tiles into local state — the user can drag / resize
// / add / remove freely; only "Done" persists via dashboardsClient.
// Cancel (or navigate away, or flip back to view) discards the
// working copy.
//
// Drag/resize are CSS-grid-cell snaps (NOT pixel offsets): we measure
// the wall's bounding rect, divide by 12 cols / 8 rows to get a per-
// cell pixel size, then convert pointer deltas into integer cell
// deltas. Drag-to-reorder swaps `(col_start, row_start)` of the
// dragging tile with whichever tile owns the target cell. Resize
// stretches the span; if the new footprint overlaps neighbors,
// `resolveCollision` shifts them by the same delta.
//
// Tile cameras: the dashboard wire model only carries `doorbot_id` per
// tile. To render real camera names + locations we kick a single
// `camerasClient.list()` (paginated by the BFF; we walk it via
// `listAll` so we don't accidentally render "Camera <id>" placeholders
// for tiles that fall on the second page). The lookup map is keyed by
// doorbot_id; missing entries fall back to a synthesized
// "Camera <short-id>" so the wall never crashes when a doorbot has
// been removed from the catalog.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError } from "@/api";
import { camerasClient, type Camera } from "@/api/camerasClient";
import { dashboardsClient } from "@/api/dashboardsClient";
import { locationsClient } from "@/api/locationsClient";
import {
  CameraTile,
  type CameraTileChip,
  type ResizeCorner,
} from "@/components/CameraTile";
import { Button, Modal, useToast } from "@/components";
import type { Dashboard, DashboardTile } from "@/types/dashboard";
import {
  DASHBOARD_GRID_COLS,
  DASHBOARD_GRID_ROWS,
  DASHBOARD_MAX_TILES,
} from "@/types/dashboard";
import { DashboardSwitcher } from "./DashboardSwitcher";
import { EventsPane } from "./EventsPane";
import { TileModal } from "./TileModal";
import { DashboardLibraryPane } from "./DashboardLibraryPane";
import {
  DEFAULT_TILE_COL_SPAN,
  DEFAULT_TILE_ROW_SPAN,
  clampToGrid,
  findEmptySlot,
  resetLayout,
  resolveCollision,
  tilesLayoutEqual,
} from "./dashboardLayout";

const RIGHTPANE_PIN_KEY = "reb.ui.prefs.rightPanePinned";

function readRightPanePinned(): boolean {
  try {
    return window.localStorage.getItem(RIGHTPANE_PIN_KEY) === "1";
  } catch {
    return false;
  }
}

function writeRightPanePinned(v: boolean): void {
  try {
    window.localStorage.setItem(RIGHTPANE_PIN_KEY, v ? "1" : "0");
  } catch {
    /* localStorage unavailable — non-fatal. */
  }
}

type Mode = "view" | "edit";

/** "view" / "edit" only; everything else is normalized to "view". */
function parseMode(raw: string | null): Mode {
  return raw === "edit" ? "edit" : "view";
}

/**
 * Build the lookup the wall uses to render tile names + locations.
 * The `Camera` shape has `description` (the friendly name) and
 * `location_id`; we resolve the location_id to a display_name via the
 * locations list. Both calls are best-effort — missing data just shows
 * up as "Camera <id>" / no subline rather than blocking the render.
 */
async function loadCatalog(signal: AbortSignal): Promise<{
  cameras: Map<string, Camera>;
  locationNames: Map<string, string>;
}> {
  const [camList, locList] = await Promise.all([
    camerasClient.listAll({ signal }).catch((err: unknown) => {
      // Swallow 403/404/network: tiles render with synthetic labels.
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        return [] as Camera[];
      }
      throw err;
    }),
    locationsClient.list(signal).catch(() => []),
  ]);
  const cameras = new Map<string, Camera>();
  for (const cam of camList) {
    cameras.set(cam.doorbot_id, cam);
  }
  const locationNames = new Map<string, string>();
  for (const loc of locList) {
    locationNames.set(loc.ring_location_id, loc.display_name);
  }
  return { cameras, locationNames };
}

/** Defensive renderable name for a tile when the catalog is missing it. */
function fallbackName(doorbotId: string): string {
  const tail = doorbotId.length > 8 ? doorbotId.slice(-6) : doorbotId;
  return `Camera ${tail}`;
}

/** Convert a Camera + location lookup into the props the tile expects. */
function tileCameraFor(
  tile: DashboardTile,
  cameras: Map<string, Camera>,
  locationNames: Map<string, string>,
): { name: string; location_name: string | null; status: Camera["status"] | null } {
  const cam = cameras.get(tile.doorbot_id) ?? null;
  if (!cam) {
    return { name: fallbackName(tile.doorbot_id), location_name: null, status: null };
  }
  const name = cam.description?.trim() || fallbackName(tile.doorbot_id);
  const locName = locationNames.get(cam.location_id) ?? null;
  return { name, location_name: locName, status: cam.status };
}

/**
 * Map a camera's status into the view-mode chip (mockup uses LOW BITRATE
 * for degraded, OFFLINE for offline, no chip for online — the "Person /
 * Motion" chips are AVA event-driven and arrive in wave 4).
 */
function chipForStatus(status: Camera["status"] | null): CameraTileChip | undefined {
  if (status === "offline") return { tone: "warn", label: "Offline" };
  if (status === "degraded") return { tone: "warn", label: "Low bitrate" };
  return undefined;
}

interface OpenTileState {
  tile: DashboardTile;
  camera: { doorbot_id: string; name: string; location_name: string | null };
  chip: CameraTileChip | undefined;
}

/** Generate a stable tile_id. Backend rejects duplicates / missing. */
function makeTileId(): string {
  // Browsers ≥ Safari 15.4, all evergreen Chromium/Firefox: crypto.randomUUID.
  const c = (globalThis as unknown as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  // Fallback (test envs) — RFC4122-ish but only used when the platform
  // doesn't expose randomUUID. The backend treats it as opaque.
  return `tile_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Active drag/resize gesture state. */
interface DragState {
  kind: "move";
  tileId: string;
  startCol: number;
  startRow: number;
  pointerId: number;
  /** Pointer client-x/y at gesture start, used to compute deltas. */
  startX: number;
  startY: number;
  /** Cached cell pixel size — updated on pointermove for safety. */
  cellW: number;
  cellH: number;
  /** Current snapped target (row-major top-left of the dragged tile). */
  curCol: number;
  curRow: number;
  /** Raw pixel offset from the gesture start — used to translate the
   *  dragged tile in pixels so it tracks the cursor smoothly between
   *  cell snaps. */
  dx: number;
  dy: number;
}
interface ResizeState {
  kind: "resize";
  tileId: string;
  corner: ResizeCorner;
  pointerId: number;
  startX: number;
  startY: number;
  cellW: number;
  cellH: number;
  // Original rect.
  origCol: number;
  origRow: number;
  origColSpan: number;
  origRowSpan: number;
  // Latest snapped rect emitted on move.
  curCol: number;
  curRow: number;
  curColSpan: number;
  curRowSpan: number;
  /** Raw pixel deltas for smooth resize tracking. */
  dx: number;
  dy: number;
}
type Gesture = DragState | ResizeState | null;

export function DashboardWallPage(): JSX.Element {
  const params = useParams<{ dashboardId: string }>();
  const dashboardId = params.dashboardId ?? "";
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = parseMode(searchParams.get("mode"));

  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [allDashboards, setAllDashboards] = useState<Dashboard[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<{
    cameras: Map<string, Camera>;
    locationNames: Map<string, string>;
  } | null>(null);
  const [openTile, setOpenTile] = useState<OpenTileState | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [createPromptOpen, setCreatePromptOpen] = useState<boolean>(false);
  const [createName, setCreateName] = useState<string>("");
  // View-mode events pane: open is transient, pinned persists.
  const [eventsPaneOpen, setEventsPaneOpen] = useState<boolean>(false);
  const [eventsPanePinned, setEventsPanePinned] = useState<boolean>(() =>
    readRightPanePinned(),
  );

  /**
   * Edit-mode working copy of the tiles. `null` while in view mode
   * (we don't want to retain stale edits when the user toggles back
   * and forth). On entering edit mode we fork from the dashboard;
   * Done writes this back through the client.
   */
  const [editTiles, setEditTiles] = useState<DashboardTile[] | null>(null);
  // Working copy of the dashboard's display_name while in edit mode.
  // null means "not editing" (the committed name from `dashboard` is shown).
  const [editName, setEditName] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const wallRef = useRef<HTMLDivElement | null>(null);
  const gestureRef = useRef<Gesture>(null);
  // Force re-render on gesture transitions (drag preview).
  const [, setGestureTick] = useState<number>(0);
  const bumpGesture = useCallback((): void => {
    setGestureTick((t) => t + 1);
  }, []);

  // Load this dashboard + the full list (so the switcher can render).
  useEffect(() => {
    const ctrl = new AbortController();
    setDashboard(null);
    setLoadError(null);
    void (async () => {
      try {
        const [dash, list] = await Promise.all([
          dashboardsClient.get(dashboardId),
          dashboardsClient.list(),
        ]);
        if (ctrl.signal.aborted) return;
        setDashboard(dash);
        setAllDashboards(list);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `${err.status} ${err.code ?? err.message}`
            : err instanceof Error
              ? err.message
              : "Failed to load dashboard";
        setLoadError(msg);
      }
    })();
    return () => ctrl.abort();
  }, [dashboardId]);

  // Camera + location catalog — independent of the dashboard fetch so we
  // don't redo it on a switch between dashboards (the catalog is shared).
  useEffect(() => {
    if (catalog !== null) return;
    const ctrl = new AbortController();
    void loadCatalog(ctrl.signal)
      .then((next) => {
        if (ctrl.signal.aborted) return;
        setCatalog(next);
      })
      .catch(() => {
        // Catalog failures are non-fatal; tiles fall back to placeholder
        // names. We still set an empty catalog so we don't keep retrying.
        if (ctrl.signal.aborted) return;
        setCatalog({ cameras: new Map(), locationNames: new Map() });
      });
    return () => ctrl.abort();
  }, [catalog]);

  // Fork / clear the edit working copy when mode changes. We intentionally
  // depend only on (mode, dashboard); a stale editTiles dep would loop
  // every time the working copy mutates.
  useEffect(() => {
    if (mode === "edit") {
      setEditTiles((prev) => (prev === null && dashboard ? dashboard.tiles.slice() : prev));
      setEditName((prev) => (prev === null && dashboard ? dashboard.display_name : prev));
    } else {
      setEditTiles((prev) => (prev === null ? prev : null));
      setEditName((prev) => (prev === null ? prev : null));
    }
  }, [mode, dashboard]);

  const handleSelectDashboard = useCallback(
    (id: string): void => {
      // Preserve mode on switch so a user who's mid-edit stays in edit
      // when flipping dashboards (matches the v6 sidebar pattern).
      const qs = mode === "edit" ? "?mode=edit" : "";
      navigate(`/dashboards/${encodeURIComponent(id)}${qs}`);
    },
    [mode, navigate],
  );

  const handleOpenCreatePrompt = useCallback((): void => {
    setCreateName("");
    setCreatePromptOpen(true);
  }, []);

  const handleCommitCreate = useCallback(async (): Promise<void> => {
    if (creating) return;
    const name = createName.trim();
    if (name.length === 0) {
      toast.danger({
        title: "Name required",
        description: "Give your dashboard a name to create it.",
      });
      return;
    }
    setCreating(true);
    try {
      const dash = await dashboardsClient.create({
        display_name: name,
        tiles: [],
      });
      toast.success({ title: `Created "${dash.display_name}"` });
      setCreatePromptOpen(false);
      setCreateName("");
      navigate(`/dashboards/${encodeURIComponent(dash.dashboard_id)}?mode=edit`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to create dashboard";
      toast.danger({ title: "Could not create dashboard", description: message });
    } finally {
      setCreating(false);
    }
  }, [createName, creating, navigate, toast]);

  const setMode = useCallback(
    (next: Mode): void => {
      // URL query is the source of truth; refresh-safe by design.
      const sp = new URLSearchParams(searchParams);
      if (next === "edit") sp.set("mode", "edit");
      else sp.delete("mode");
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleResetLayout = useCallback((): void => {
    if (mode !== "edit") return;
    setEditTiles((prev) => (prev === null ? prev : resetLayout(prev)));
  }, [mode]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (saving) return;
    if (!dashboard || editTiles === null) {
      // Nothing to save → just flip to view.
      setMode("view");
      return;
    }
    const trimmedName = (editName ?? dashboard.display_name).trim();
    if (trimmedName.length === 0) {
      toast.danger({
        title: "Name required",
        description: "Give your dashboard a name to save it.",
      });
      return;
    }
    const tilesUnchanged = tilesLayoutEqual(editTiles, dashboard.tiles);
    const nameUnchanged = trimmedName === dashboard.display_name;
    if (tilesUnchanged && nameUnchanged) {
      setMode("view");
      return;
    }
    setSaving(true);
    try {
      const updated = await dashboardsClient.update(
        dashboard.dashboard_id,
        {
          ...(tilesUnchanged ? {} : { tiles: editTiles }),
          ...(nameUnchanged ? {} : { display_name: trimmedName }),
        },
        dashboard.version,
      );
      setDashboard(updated);
      setEditTiles(null);
      setEditName(null);
      toast.success({ title: "Dashboard saved" });
      setMode("view");
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to save dashboard";
      toast.danger({ title: "Could not save dashboard", description: msg });
    } finally {
      setSaving(false);
    }
  }, [saving, dashboard, editTiles, editName, setMode, toast]);

  // Source of truth for the tile array currently rendered.
  const tilesToRender: DashboardTile[] = useMemo(() => {
    if (mode === "edit" && editTiles !== null) {
      return editTiles.slice(0, DASHBOARD_MAX_TILES);
    }
    if (!dashboard) return [];
    // Hard cap per LLD §4 — backend validates, but we slice defensively.
    return dashboard.tiles.slice(0, DASHBOARD_MAX_TILES);
  }, [dashboard, mode, editTiles]);

  const dirty: boolean = useMemo(() => {
    if (mode !== "edit" || editTiles === null || !dashboard) return false;
    if (!tilesLayoutEqual(editTiles, dashboard.tiles)) return true;
    if (editName !== null && editName.trim() !== dashboard.display_name) return true;
    return false;
  }, [mode, editTiles, editName, dashboard]);

  const handleTileClick = useCallback(
    (tile: DashboardTile): void => {
      if (mode !== "view") return;
      if (!catalog) return;
      const lookup = tileCameraFor(tile, catalog.cameras, catalog.locationNames);
      setOpenTile({
        tile,
        camera: {
          doorbot_id: tile.doorbot_id,
          name: lookup.name,
          location_name: lookup.location_name,
        },
        chip: chipForStatus(lookup.status),
      });
    },
    [catalog, mode],
  );

  const handleRemoveTile = useCallback(
    (tileId: string): void => {
      if (mode !== "edit") return;
      setEditTiles((prev) =>
        prev === null ? prev : prev.filter((t) => t.tile_id !== tileId),
      );
    },
    [mode],
  );

  const handleAddCameraByDoorbot = useCallback(
    (doorbotId: string): void => {
      if (mode !== "edit") return;
      setEditTiles((prev) => {
        if (prev === null) return prev;
        if (prev.length >= DASHBOARD_MAX_TILES) {
          toast.warning({
            title: `Dashboard is full (${DASHBOARD_MAX_TILES} max)`,
          });
          return prev;
        }
        const slot = findEmptySlot(
          prev,
          DEFAULT_TILE_COL_SPAN,
          DEFAULT_TILE_ROW_SPAN,
        );
        if (!slot) {
          toast.warning({ title: "No room on the wall for this camera" });
          return prev;
        }
        return [
          ...prev,
          {
            tile_id: makeTileId(),
            doorbot_id: doorbotId,
            col_start: slot.col_start,
            row_start: slot.row_start,
            col_span: DEFAULT_TILE_COL_SPAN,
            row_span: DEFAULT_TILE_ROW_SPAN,
          },
        ];
      });
    },
    [mode, toast],
  );

  const handleAddLocation = useCallback(
    (doorbotIds: string[]): void => {
      if (mode !== "edit") return;
      setEditTiles((prev) => {
        if (prev === null) return prev;
        let placed = 0;
        let skipped = 0;
        const next = prev.slice();
        for (const id of doorbotIds) {
          if (next.length >= DASHBOARD_MAX_TILES) {
            skipped++;
            continue;
          }
          const slot = findEmptySlot(
            next,
            DEFAULT_TILE_COL_SPAN,
            DEFAULT_TILE_ROW_SPAN,
          );
          if (!slot) {
            skipped++;
            continue;
          }
          next.push({
            tile_id: makeTileId(),
            doorbot_id: id,
            col_start: slot.col_start,
            row_start: slot.row_start,
            col_span: DEFAULT_TILE_COL_SPAN,
            row_span: DEFAULT_TILE_ROW_SPAN,
          });
          placed++;
        }
        if (skipped > 0) {
          toast.warning({
            title: "Some cameras did not fit",
            description: `Added ${placed}, skipped ${skipped}.`,
          });
        }
        return next;
      });
    },
    [mode, toast],
  );

  const handleRemoveByDoorbot = useCallback(
    (doorbotId: string): void => {
      if (mode !== "edit") return;
      setEditTiles((prev) =>
        prev === null ? prev : prev.filter((t) => t.doorbot_id !== doorbotId),
      );
    },
    [mode],
  );

  // ----------- Pointer-driven drag/resize gesture handlers ------------
  // The wall element's bounding rect, divided by 12×8, gives us the
  // per-cell pixel size. We snap pointer deltas into integer cell
  // deltas; only when the snapped value changes do we mutate state.

  const wallCellSize = useCallback((): { cellW: number; cellH: number } | null => {
    const el = wallRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    // Use computed style padding so the inner grid matches the actual
    // grid track size. v7-wall has 14px padding all around.
    const cs = window.getComputedStyle(el);
    const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const innerW = Math.max(0, rect.width - padX);
    const innerH = Math.max(0, rect.height - padY);
    const cellW = innerW / DASHBOARD_GRID_COLS;
    const cellH = innerH / DASHBOARD_GRID_ROWS;
    if (cellW <= 0 || cellH <= 0) return null;
    return { cellW, cellH };
  }, []);

  const handleBodyPointerDown = useCallback(
    (tile: DashboardTile, e: ReactPointerEvent<HTMLDivElement>): void => {
      if (mode !== "edit") return;
      if (e.button !== 0) return; // left-click only
      const cs = wallCellSize();
      if (!cs) return;
      // Capture so the browser keeps sending events even when the
      // pointer briefly leaves the tile during the drag.
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Some browsers throw if capture isn't permitted; safe to ignore.
      }
      gestureRef.current = {
        kind: "move",
        tileId: tile.tile_id,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        cellW: cs.cellW,
        cellH: cs.cellH,
        startCol: tile.col_start,
        startRow: tile.row_start,
        curCol: tile.col_start,
        curRow: tile.row_start,
        dx: 0,
        dy: 0,
      };
      bumpGesture();
    },
    [mode, wallCellSize, bumpGesture],
  );

  const handleResizePointerDown = useCallback(
    (
      tile: DashboardTile,
      corner: ResizeCorner,
      e: ReactPointerEvent<HTMLSpanElement>,
    ): void => {
      if (mode !== "edit") return;
      if (e.button !== 0) return;
      const cs = wallCellSize();
      if (!cs) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Same as above.
      }
      gestureRef.current = {
        kind: "resize",
        tileId: tile.tile_id,
        corner,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        cellW: cs.cellW,
        cellH: cs.cellH,
        origCol: tile.col_start,
        origRow: tile.row_start,
        origColSpan: tile.col_span,
        origRowSpan: tile.row_span,
        curCol: tile.col_start,
        curRow: tile.row_start,
        curColSpan: tile.col_span,
        curRowSpan: tile.row_span,
        dx: 0,
        dy: 0,
      };
      bumpGesture();
    },
    [mode, wallCellSize, bumpGesture],
  );

  // Window-level pointermove/up so the gesture survives the pointer
  // briefly leaving the tile (which it does as soon as the user grows
  // a tile past its current footprint).
  useEffect(() => {
    if (mode !== "edit") return;

    const onMove = (e: PointerEvent): void => {
      const g = gestureRef.current;
      if (!g) return;
      if (g.pointerId !== e.pointerId) return;
      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;
      // Always record raw deltas — the dragged tile's CSS transform
      // reads these every frame so it tracks the cursor between
      // cell snaps.
      g.dx = dx;
      g.dy = dy;
      if (g.kind === "move") {
        const dCol = Math.round(dx / g.cellW);
        const dRow = Math.round(dy / g.cellH);
        const targetCol = g.startCol + dCol;
        const targetRow = g.startRow + dRow;
        g.curCol = targetCol;
        g.curRow = targetRow;
        bumpGesture();
        return;
      }
      // Resize
      const dCol = Math.round(dx / g.cellW);
      const dRow = Math.round(dy / g.cellH);
      let col = g.origCol;
      let row = g.origRow;
      let colSpan = g.origColSpan;
      let rowSpan = g.origRowSpan;
      if (g.corner === "br") {
        colSpan = g.origColSpan + dCol;
        rowSpan = g.origRowSpan + dRow;
      } else if (g.corner === "tr") {
        colSpan = g.origColSpan + dCol;
        row = g.origRow + dRow;
        rowSpan = g.origRowSpan - dRow;
      } else if (g.corner === "bl") {
        col = g.origCol + dCol;
        colSpan = g.origColSpan - dCol;
        rowSpan = g.origRowSpan + dRow;
      } else {
        // tl
        col = g.origCol + dCol;
        colSpan = g.origColSpan - dCol;
        row = g.origRow + dRow;
        rowSpan = g.origRowSpan - dRow;
      }
      const clamped = clampToGrid({
        col_start: col,
        row_start: row,
        col_span: colSpan,
        row_span: rowSpan,
      });
      g.curCol = clamped.col_start;
      g.curRow = clamped.row_start;
      g.curColSpan = clamped.col_span;
      g.curRowSpan = clamped.row_span;
      bumpGesture();
    };

    const onUp = (e: PointerEvent): void => {
      const g = gestureRef.current;
      if (!g) return;
      if (g.pointerId !== e.pointerId) return;
      // Commit the gesture. We do the actual mutation here (rather
      // than on every move) so the React state stays simple; the move
      // handler only updates the gesture-ref preview.
      gestureRef.current = null;
      setEditTiles((prev) => {
        if (prev === null) return prev;
        if (g.kind === "move") {
          const dCol = g.curCol - g.startCol;
          const dRow = g.curRow - g.startRow;
          if (dCol === 0 && dRow === 0) return prev;
          const moving = prev.find((t) => t.tile_id === g.tileId);
          if (!moving) return prev;
          try {
            return resolveCollision(prev, moving, {
              col_start: g.curCol,
              row_start: g.curRow,
              col_span: moving.col_span,
              row_span: moving.row_span,
            });
          } catch {
            return prev;
          }
        }
        // Resize
        const moving = prev.find((t) => t.tile_id === g.tileId);
        if (!moving) return prev;
        if (
          g.curCol === g.origCol &&
          g.curRow === g.origRow &&
          g.curColSpan === g.origColSpan &&
          g.curRowSpan === g.origRowSpan
        ) {
          return prev;
        }
        try {
          return resolveCollision(prev, moving, {
            col_start: g.curCol,
            row_start: g.curRow,
            col_span: g.curColSpan,
            row_span: g.curRowSpan,
          });
        } catch {
          return prev;
        }
      });
      bumpGesture();
    };

    const onCancel = (): void => {
      if (gestureRef.current) {
        gestureRef.current = null;
        bumpGesture();
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [mode, bumpGesture]);

  // Build crumbs string ("ZAS6 — Austin, TX + 2 more"). Best-effort —
  // an empty dashboard gets no crumb tail.
  const crumbsLabel = useMemo<string>(() => {
    if (!dashboard || !catalog) return "";
    const seen: string[] = [];
    for (const tile of dashboard.tiles) {
      const cam = catalog.cameras.get(tile.doorbot_id);
      if (!cam) continue;
      const loc = catalog.locationNames.get(cam.location_id);
      if (loc && !seen.includes(loc)) seen.push(loc);
    }
    if (seen.length === 0) return "";
    if (seen.length === 1) return seen[0]!;
    if (seen.length === 2) return `${seen[0]} · ${seen[1]}`;
    return `${seen[0]} + ${seen.length - 1} more`;
  }, [dashboard, catalog]);

  return (
    <div className="v7-surface-root">
      <DashboardSwitcher
        dashboards={allDashboards}
        activeId={dashboard?.dashboard_id ?? null}
        onSelect={handleSelectDashboard}
        onCreate={handleOpenCreatePrompt}
      />

      {loadError && (
        <div
          className="v7-overview__error"
          role="alert"
          style={{ margin: 16 }}
        >
          {loadError}
        </div>
      )}

      {dashboard !== null && (
        <>
          <div className="v7-mode-bar">
            {mode === "edit" ? (
              <input
                type="text"
                className="v7-mode-bar__title-input"
                value={editName ?? dashboard.display_name}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    (e.currentTarget as HTMLInputElement).blur();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setEditName(dashboard.display_name);
                    (e.currentTarget as HTMLInputElement).blur();
                  }
                }}
                aria-label="Dashboard name"
                placeholder="Dashboard name"
                spellCheck={false}
                maxLength={120}
              />
            ) : (
              <span className="v7-mode-bar__title">{dashboard.display_name}</span>
            )}
            {crumbsLabel && (
              <span className="v7-mode-bar__crumbs">· {crumbsLabel}</span>
            )}
            {mode === "edit" && dirty && (
              <span
                className="v7-mode-bar__dirty"
                title="You have unsaved layout changes"
                role="status"
              >
                Unsaved changes
              </span>
            )}
            <span className="v7-mode-bar__spacer" />
            <div
              className={`v7-mode-toggle${mode === "edit" ? " is-edit" : ""}`}
              role="tablist"
              aria-label="Dashboard mode"
            >
              <span className="v7-mode-toggle__knob" aria-hidden="true" />
              <button
                type="button"
                role="tab"
                aria-selected={mode === "view"}
                className={mode === "view" ? "is-active" : ""}
                onClick={() => setMode("view")}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 5C7 5 2.7 8.1 1 12.5 2.7 16.9 7 20 12 20s9.3-3.1 11-7.5C21.3 8.1 17 5 12 5zm0 12.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                </svg>
                View
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === "edit"}
                className={mode === "edit" ? "is-active" : ""}
                onClick={() => setMode("edit")}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25zM20.7 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0L15.13 5.13l3.75 3.75 1.82-1.84z" />
                </svg>
                Edit
              </button>
            </div>
            {mode === "edit" && (
              <>
                <button
                  type="button"
                  className="v7-ghost-btn"
                  onClick={handleResetLayout}
                  disabled={editTiles === null || editTiles.length === 0}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 5V2L8 6l4 4V7c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6H4c0 4.4 3.6 8 8 8s8-3.6 8-8-3.6-8-8-8z" />
                  </svg>
                  Reset layout
                </button>
                <button
                  type="button"
                  className="v7-ghost-btn v7-ghost-btn--primary"
                  onClick={() => void handleSave()}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Done"}
                </button>
              </>
            )}
          </div>

          <div
            className={`v7-wall-frame${
              mode === "edit"
                ? " right-pane-open"
                : eventsPaneOpen || eventsPanePinned
                  ? " right-pane-open"
                  : ""
            }`}
          >
            {tilesToRender.length === 0 ? (
              <div className={`v7-wall v7-wall--empty${mode === "edit" ? " is-edit" : ""}`}>
                <p>
                  {mode === "edit"
                    ? "Add cameras from the library on the right."
                    : "No cameras on this dashboard yet — switch to Edit mode to add some."}
                </p>
              </div>
            ) : (
              (() => {
                // ----- Live drag/resize preview ---------------------
                // Every tile renders at its COMMITTED grid cell so
                // the wall's grid layout never changes mid-gesture.
                // We then translate non-gesture tiles by (preview -
                // committed) cell-deltas; CSS interpolates between
                // delta changes, giving a smooth slide-out without
                // any FLIP rect-reads (which were causing flicker
                // when transitions stomped on each other).
                const gesture = gestureRef.current;
                const previewByTileId = new Map<
                  string,
                  { col_start: number; row_start: number }
                >();
                let placeholder: {
                  col: number;
                  row: number;
                  colSpan: number;
                  rowSpan: number;
                } | null = null;
                if (gesture) {
                  const target = tilesToRender.find(
                    (t) => t.tile_id === gesture.tileId,
                  );
                  if (target) {
                    let pos: {
                      col_start: number;
                      row_start: number;
                      col_span: number;
                      row_span: number;
                    };
                    if (gesture.kind === "move") {
                      pos = clampToGrid({
                        col_start: gesture.curCol,
                        row_start: gesture.curRow,
                        col_span: target.col_span,
                        row_span: target.row_span,
                      });
                    } else {
                      pos = clampToGrid({
                        col_start: gesture.curCol,
                        row_start: gesture.curRow,
                        col_span: gesture.curColSpan,
                        row_span: gesture.curRowSpan,
                      });
                    }
                    placeholder = {
                      col: pos.col_start,
                      row: pos.row_start,
                      colSpan: pos.col_span,
                      rowSpan: pos.row_span,
                    };
                    try {
                      const next = resolveCollision(tilesToRender, target, pos);
                      for (const t of next) {
                        if (t.tile_id === target.tile_id) continue;
                        previewByTileId.set(t.tile_id, {
                          col_start: t.col_start,
                          row_start: t.row_start,
                        });
                      }
                    } catch {
                      // No-op: treat as no preview, neighbors stay put.
                    }
                  }
                }
                return (
                  <div
                    className={`v7-wall${mode === "edit" ? " is-edit" : ""}${
                      gesture ? " is-dragging" : ""
                    }`}
                    ref={wallRef}
                  >
                    {placeholder && (
                      <div
                        className="v7-wall-placeholder"
                        aria-hidden="true"
                        style={{
                          gridColumn: `${placeholder.col} / span ${placeholder.colSpan}`,
                          gridRow: `${placeholder.row} / span ${placeholder.rowSpan}`,
                        }}
                      />
                    )}
                    {tilesToRender.map((tile) => {
                      const lookup = catalog
                        ? tileCameraFor(
                            tile,
                            catalog.cameras,
                            catalog.locationNames,
                          )
                        : {
                            name: fallbackName(tile.doorbot_id),
                            location_name: null,
                            status: null,
                          };
                      const isDragging =
                        gesture !== null &&
                        gesture.kind === "move" &&
                        gesture.tileId === tile.tile_id;
                      const isResizing =
                        gesture !== null &&
                        gesture.kind === "resize" &&
                        gesture.tileId === tile.tile_id;
                      // Anchor every tile to its committed cell — the
                      // grid layout stays stable for the whole gesture.
                      const tileStyle: CSSProperties = {
                        gridColumn: `${tile.col_start} / span ${tile.col_span}`,
                        gridRow: `${tile.row_start} / span ${tile.row_span}`,
                      };
                      if (isDragging && gesture) {
                        // Dragged tile: track the cursor in pixels.
                        tileStyle.transform = `translate(${gesture.dx}px, ${gesture.dy}px)`;
                        tileStyle.zIndex = 10;
                        tileStyle.transition = "none";
                        tileStyle.willChange = "transform";
                      } else if (
                        isResizing &&
                        gesture &&
                        gesture.kind === "resize"
                      ) {
                        // Resizing tile: rubber-band the affected edges.
                        const cellW = gesture.cellW;
                        const cellH = gesture.cellH;
                        const dxL =
                          gesture.corner === "tl" || gesture.corner === "bl"
                            ? gesture.dx
                            : 0;
                        const dxR =
                          gesture.corner === "tr" || gesture.corner === "br"
                            ? gesture.dx
                            : 0;
                        const dyT =
                          gesture.corner === "tl" || gesture.corner === "tr"
                            ? gesture.dy
                            : 0;
                        const dyB =
                          gesture.corner === "bl" || gesture.corner === "br"
                            ? gesture.dy
                            : 0;
                        const baseW = gesture.origColSpan * cellW;
                        const baseH = gesture.origRowSpan * cellH;
                        const newW = Math.max(cellW, baseW + dxR - dxL);
                        const newH = Math.max(cellH, baseH + dyB - dyT);
                        const tx = (dxL + dxR) / 2;
                        const ty = (dyT + dyB) / 2;
                        tileStyle.transform = `translate(${tx}px, ${ty}px) scale(${newW / baseW}, ${newH / baseH})`;
                        tileStyle.transformOrigin = "center center";
                        tileStyle.zIndex = 10;
                        tileStyle.transition = "none";
                        tileStyle.willChange = "transform";
                      } else if (gesture) {
                        // Non-gesture tile: if the preview moved it,
                        // express the move as a translate so CSS can
                        // smoothly interpolate between cell-snaps.
                        const preview = previewByTileId.get(tile.tile_id);
                        if (
                          preview &&
                          (preview.col_start !== tile.col_start ||
                            preview.row_start !== tile.row_start)
                        ) {
                          const dCol = preview.col_start - tile.col_start;
                          const dRow = preview.row_start - tile.row_start;
                          tileStyle.transform = `translate(${dCol * gesture.cellW}px, ${dRow * gesture.cellH}px)`;
                          tileStyle.willChange = "transform";
                        }
                      }
                      // The pointer-down handlers need the committed tile
                      // shape (pre-preview) so gesture.startCol/startRow
                      // reflect where the tile actually lives in state.
                      return (
                        <div
                          key={tile.tile_id}
                          className={`v7-tile${
                            isDragging ? " is-dragging" : ""
                          }${isResizing ? " is-resizing" : ""}`}
                          style={tileStyle}
                        >
                          <CameraTile
                            camera={{
                              doorbot_id: tile.doorbot_id,
                              name: lookup.name,
                              location_name: lookup.location_name,
                            }}
                            mode={mode}
                            isOffline={lookup.status === "offline"}
                            chip={chipForStatus(lookup.status)}
                            isGhost={false}
                            onClick={
                              mode === "view"
                                ? () => handleTileClick(tile)
                                : undefined
                            }
                            onRemove={
                              mode === "edit"
                                ? () => handleRemoveTile(tile.tile_id)
                                : undefined
                            }
                            onBodyPointerDown={
                              mode === "edit"
                                ? (e) => handleBodyPointerDown(tile, e)
                                : undefined
                            }
                            onResizePointerDown={
                              mode === "edit"
                                ? (corner, e) =>
                                    handleResizePointerDown(tile, corner, e)
                                : undefined
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}

            {mode === "edit" && editTiles !== null && (
              <DashboardLibraryPane
                tiles={editTiles}
                onAddCamera={handleAddCameraByDoorbot}
                onAddLocation={handleAddLocation}
                onRemoveCamera={handleRemoveByDoorbot}
              />
            )}
            {mode === "view" && catalog && (
              <EventsPane
                tiles={tilesToRender}
                catalog={catalog}
                isOpen={eventsPaneOpen}
                isPinned={eventsPanePinned}
                onToggleOpen={() => setEventsPaneOpen((v) => !v)}
                onTogglePinned={() => {
                  setEventsPanePinned((prev) => {
                    const next = !prev;
                    writeRightPanePinned(next);
                    if (next) setEventsPaneOpen(true);
                    return next;
                  });
                }}
              />
            )}
          </div>
        </>
      )}

      {openTile && (
        <TileModal
          tile={openTile.tile}
          camera={openTile.camera}
          chip={openTile.chip}
          onClose={() => setOpenTile(null)}
        />
      )}

      <Modal
        open={createPromptOpen}
        onClose={() => {
          if (!creating) setCreatePromptOpen(false);
        }}
        title="Create dashboard"
        description="Give your new camera wall a name. You can rename it later."
        variant="confirm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCreatePromptOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleCommitCreate()}
              disabled={creating || createName.trim().length === 0}
            >
              {creating ? "Creating…" : "Create"}
            </Button>
          </>
        }
      >
        <input
          type="text"
          autoFocus
          className="v7-create-input"
          placeholder="e.g. Front-of-house"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && createName.trim().length > 0 && !creating) {
              e.preventDefault();
              void handleCommitCreate();
            }
          }}
          aria-label="Dashboard name"
        />
      </Modal>
    </div>
  );
}
